(() => {
  if (window.customElements.get("native-product-comparison")) {
    return;
  }

  const GROUPS = [
    {
      name: "Prebuilt Gaming PCs",
      rows: [{ label: "Price", key: "price" }],
    },
    {
      name: "Core Specs",
      rows: [
        { label: "CPU", key: "cpu" },
        { label: "GPU", key: "gpu" },
        { label: "Memory", key: "memory" },
        { label: "Storage", key: "storage" },
      ],
    },
    {
      name: "Cooling & Power",
      rows: [
        { label: "CPU Cooler", key: "cpuCooler" },
        { label: "Fans", key: "fans", display: "lines" },
        { label: "Power Supply", key: "powerSupply" },
        { label: "Case", key: "case" },
      ],
    },
    {
      name: "Connectivity",
      rows: [
        { label: "Wi-Fi", key: "wifi" },
        { label: "Front I/O ports", key: "frontIo", display: "list" },
        { label: "Rear I/O Ports", key: "rearIo", display: "list" },
      ],
    },
    {
      name: "Others",
      rows: [
        { label: "Operating System", key: "operatingSystem" },
        { label: "Warranty", key: "warranty" },
        { label: "Best For", key: "bestFor" },
      ],
    },
  ];

  const normalize = (value) => String(value || "").trim().toLowerCase();
  const isMeaningfulValue = (value) => !["", "n/a", "na", "none"].includes(normalize(value));

  const asArray = (value) => {
    if (Array.isArray(value)) {
      return value.filter(Boolean).map(String);
    }

    if (value === null || value === undefined || value === "") {
      return [];
    }

    return [String(value)];
  };

  const categoriesFor = (product) => {
    let techSpecs = product.techSpecs;

    if (typeof techSpecs === "string") {
      try {
        techSpecs = JSON.parse(techSpecs);
      } catch (_error) {
        return [];
      }
    }

    return Array.isArray(techSpecs?.categories) ? techSpecs.categories : [];
  };

  const findCategory = (product, names) => {
    const normalizedNames = names.map(normalize);
    return categoriesFor(product).find((category) => normalizedNames.includes(normalize(category?.name)));
  };

  const findSpec = (product, categoryNames, specNames) => {
    const category = findCategory(product, categoryNames);
    const normalizedSpecNames = specNames.map(normalize);
    const spec = category?.specs?.find((item) => normalizedSpecNames.includes(normalize(item?.key)));
    return spec?.value || "";
  };

  const splitPorts = (value) => {
    if (Array.isArray(value)) {
      return value.filter(Boolean).map(String);
    }

    return asArray(value).flatMap((item) => item.split(/\s+\/\s+/).filter(Boolean));
  };

  const fanSpecs = (product) => {
    const fanCategories = [
      { names: ["Cooler Fan", "Cooler Fans"], label: "Cooler Fan" },
      { names: ["Case Fan - Front", "Case Fans (Front)"], label: "Front" },
      { names: ["Case Fan - Bottom", "Case Fans (Bottom)"], label: "Bottom" },
      { names: ["Case Fan - Rear", "Case Fans (Rear)"], label: "Rear" },
    ];

    return fanCategories.flatMap(({ names, label }) => {
      const model = findSpec(product, names, ["Model"]);
      return isMeaningfulValue(model) ? [`${label}: ${model}`] : [];
    });
  };

  const valuesFor = (product) => ({
    price: product.price,
    cpu: findSpec(product, ["Key Specs"], ["CPU"]),
    gpu: findSpec(product, ["Key Specs"], ["GPU"]),
    memory: findSpec(product, ["Key Specs"], ["RAM", "Memory"]),
    storage: findSpec(product, ["Key Specs"], ["Storage"]),
    cpuCooler: findSpec(product, ["CPU Cooler"], ["Model"]),
    fans: fanSpecs(product),
    powerSupply: findSpec(product, ["Power Supply", "Power"], ["Model"]),
    case: findSpec(product, ["Case"], ["Model"]),
    wifi: findSpec(product, ["Motherboard"], ["Wi-Fi", "Wifi"]),
    frontIo: splitPorts(findSpec(product, ["Case"], ["Front I/O", "Front I/O ports"])),
    rearIo: asArray(product.additionalSpecs?.rearIo),
    operatingSystem: findSpec(product, ["Key Specs", "Software"], ["Operating System"]),
    warranty: findSpec(product, ["Warranty"], ["Parts", "Manufacturer's Warranty - Parts"]),
    bestFor: product.additionalSpecs?.bestFor || "",
  });

  class NativeProductComparison extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") {
        return;
      }

      const dataElement = this.querySelector("[data-comparison-products]");
      const root = this.querySelector("[data-comparison-root]");

      if (!dataElement || !root) {
        return;
      }

      try {
        this.products = JSON.parse(dataElement.textContent);
      } catch (_error) {
        root.textContent = "Product comparison is temporarily unavailable.";
        return;
      }

      if (!Array.isArray(this.products) || this.products.length === 0) {
        return;
      }

      this.root = root;
      this.visibleCount = Math.min(
        Math.max(Number.parseInt(this.dataset.visibleProducts, 10) || 3, 2),
        this.products.length,
      );
      this.showRatings = this.dataset.showRatings === "true";
      this.showDetails = this.dataset.showDetails === "true";
      this.selectedHandles = this.products.slice(0, this.visibleCount).map((product) => product.handle);
      this.dataset.initialized = "true";
      this.render();
    }

    get selectedProducts() {
      return this.selectedHandles.map(
        (handle) => this.products.find((product) => product.handle === handle) || this.products[0],
      );
    }

    createProductHeader(product, columnIndex) {
      const cell = document.createElement("th");
      cell.className = "native-product-comparison__product";
      cell.scope = "col";

      const select = document.createElement("select");
      select.className = "native-product-comparison__select";
      select.setAttribute("aria-label", `Choose a product for comparison column ${columnIndex + 1}`);
      select.dataset.columnIndex = String(columnIndex);

      this.products.forEach((optionProduct) => {
        const option = document.createElement("option");
        option.value = optionProduct.handle;
        option.textContent = optionProduct.title;
        option.selected = optionProduct.handle === product.handle;
        select.append(option);
      });

      cell.append(select);

      if (product.image?.url) {
        const imageLink = document.createElement("a");
        imageLink.className = "native-product-comparison__image-link";
        imageLink.href = product.url;

        const image = document.createElement("img");
        image.className = "native-product-comparison__image";
        image.src = product.image.url;
        image.alt = product.image.alt || product.title;
        image.width = product.image.width || 600;
        image.height = product.image.height || 600;
        image.loading = "lazy";
        imageLink.append(image);
        cell.append(imageLink);
      }

      const title = document.createElement("a");
      title.className = "native-product-comparison__title";
      title.href = product.url;
      title.textContent = product.title;
      cell.append(title);

      const price = document.createElement("span");
      price.className = "native-product-comparison__price";
      price.textContent = product.price;
      cell.append(price);

      if (this.showRatings && Number(product.ratingCount) > 0) {
        cell.append(this.createRating(product));
      }

      if (this.showDetails) {
        const details = document.createElement("a");
        details.className = "native-product-comparison__details";
        details.href = product.url;
        details.textContent = "View details";
        cell.append(details);
      }

      return cell;
    }

    createRating(product) {
      const rating = Math.max(0, Math.min(Number(product.rating) || 0, Number(product.ratingMax) || 5));
      const ratingMax = Number(product.ratingMax) || 5;
      const wrapper = document.createElement("span");
      wrapper.className = "native-product-comparison__rating";
      wrapper.setAttribute("aria-label", `${rating} out of ${ratingMax} stars, ${product.ratingCount} reviews`);

      const stars = document.createElement("span");
      stars.className = "native-product-comparison__stars";
      stars.setAttribute("aria-hidden", "true");

      const emptyStars = document.createElement("span");
      emptyStars.className = "native-product-comparison__stars-empty";
      emptyStars.textContent = "★★★★★";

      const filledStars = document.createElement("span");
      filledStars.className = "native-product-comparison__stars-filled";
      filledStars.textContent = "★★★★★";
      filledStars.style.width = `${(rating / ratingMax) * 100}%`;

      const count = document.createElement("span");
      count.className = "native-product-comparison__rating-count";
      count.textContent = `(${product.ratingCount})`;

      stars.append(emptyStars, filledStars);
      wrapper.append(stars, count);
      return wrapper;
    }

    createValueCell(value, display) {
      const cell = document.createElement("td");
      cell.className = "native-product-comparison__value";
      const values = asArray(value);

      if (values.length === 0) {
        cell.textContent = "\u2014";
        return cell;
      }

      if (display === "list") {
        const list = document.createElement("ul");
        list.className = "native-product-comparison__list";
        values.forEach((item) => {
          const listItem = document.createElement("li");
          listItem.textContent = item;
          list.append(listItem);
        });
        cell.append(list);
        return cell;
      }

      if (display === "lines") {
        const lines = document.createElement("div");
        lines.className = "native-product-comparison__lines";
        values.forEach((item) => {
          const line = document.createElement("span");
          line.textContent = item;
          lines.append(line);
        });
        cell.append(lines);
        return cell;
      }

      cell.textContent = values[0];
      return cell;
    }

    render() {
      const selectedProducts = this.selectedProducts;
      const selectedValues = selectedProducts.map(valuesFor);
      const scroller = document.createElement("div");
      scroller.className = "native-product-comparison__scroller";

      const table = document.createElement("table");
      table.className = "native-product-comparison__table";
      table.style.setProperty("--comparison-visible-products", this.visibleCount);
      table.style.setProperty("--comparison-desktop-min-width", `${180 + this.visibleCount * 220}px`);
      table.style.setProperty(
        "--comparison-mobile-min-width",
        `${this.visibleCount * 160 + (this.visibleCount - 1) * 12}px`,
      );

      const caption = document.createElement("caption");
      caption.className = "sr-only";
      caption.textContent = `Specification comparison between products ${this.products
        .map((product) => product.title)
        .join(", ")}`;
      table.append(caption);

      const head = document.createElement("thead");
      const productRow = document.createElement("tr");
      productRow.className = "native-product-comparison__product-row";
      const emptyCell = document.createElement("td");
      emptyCell.className = "native-product-comparison__sticky-column";
      emptyCell.setAttribute("aria-hidden", "true");
      productRow.append(emptyCell);
      selectedProducts.forEach((product, index) => productRow.append(this.createProductHeader(product, index)));
      head.append(productRow);
      table.append(head);

      GROUPS.forEach((group) => {
        const body = document.createElement("tbody");
        const groupRow = document.createElement("tr");
        groupRow.className = "native-product-comparison__group-row";
        const groupHeading = document.createElement("th");
        groupHeading.colSpan = this.visibleCount + 1;
        groupHeading.scope = "colgroup";
        groupHeading.textContent = group.name;
        groupRow.append(groupHeading);
        body.append(groupRow);

        group.rows.forEach((rowDefinition) => {
          const row = document.createElement("tr");
          const label = document.createElement("th");
          label.className = "native-product-comparison__label native-product-comparison__sticky-column";
          label.scope = "row";
          label.textContent = rowDefinition.label;
          row.append(label);
          selectedValues.forEach((values) =>
            row.append(this.createValueCell(values[rowDefinition.key], rowDefinition.display)),
          );
          body.append(row);
        });

        table.append(body);
      });

      scroller.append(table);
      this.root.replaceChildren(scroller);

      const selects = table.querySelectorAll("select[data-column-index]");
      selects.forEach((select) => {
        select.addEventListener("change", (event) => {
          const columnIndex = Number.parseInt(event.currentTarget.dataset.columnIndex, 10);
          const previousScrollLeft = scroller.scrollLeft;
          this.selectedHandles[columnIndex] = event.currentTarget.value;
          this.render();
          this.root.querySelector(".native-product-comparison__scroller").scrollLeft = previousScrollLeft;
        });
      });
    }
  }

  window.customElements.define("native-product-comparison", NativeProductComparison);
})();
